import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';

import { useReveal } from '../hooks/useReveal';
import { Ic } from '../utils/icons';
import { authFetch } from './api';

import Nav from './Nav';
import Footer from './Footer';
import Eyebrow from './Eyebrow';

// Section 02 Assets
import microscopeImg from '../assets/Landing_Page/microscope.png';
import boxImg from '../assets/Landing_Page/box.png';
import rocketImg from '../assets/Landing_Page/rocket.png';

// Section 03 Assets
import eMobilityImg from '../assets/Landing_Page/e_mobility.png';
import factoryImg from '../assets/Landing_Page/factory.png';


// Section 05 Assets
import chipImg from '../assets/Landing_Page/chip.png';
import cloudImg from '../assets/Landing_Page/cloud.png';
import analysisImg from '../assets/Landing_Page/analysis.png';
import iotImg from '../assets/Landing_Page/iot.png';
import dashboardImg from '../assets/Landing_Page/dashboard.png';// Section 06 Assets
import discoverImg from '../assets/Landing_Page/DISCOVER.png';
import researchImg from '../assets/Landing_Page/research.png';
import validateImg from '../assets/Landing_Page/validit.png';
import optimizeImg from '../assets/Landing_Page/optimize.png';

// Hero Slider Mockups
import cmsImg from '../assets/Landing_Page/CMS.png';
import pmmsImg from '../assets/Landing_Page/PMMS.png';
import mmmsImg from '../assets/Landing_Page/MMMS.png';

const { arrow: Arrow, check: Check } = Ic;

export default function CentralHome() {
  useReveal();

  // Hero Announcement Slider state
  const [activeSlide, setActiveSlide] = useState(0);
  const slides = [
    {
      title: 'Chargify CMS',
      img: cmsImg,
      status: 'Live',
      statusColor: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      link: 'https://chargify.co.in',
      isExternal: true
    },
    {
      title: 'PMMS',
      img: pmmsImg,
      status: 'Live',
      statusColor: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      link: 'https://pmms.emberonlabs.in',
      isExternal: true
    },
    {
      title: 'MMMS',
      img: mmmsImg,
      status: 'Live',
      statusColor: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      link: 'https://mmms.emberonlabs.in',
      isExternal: true
    }
  ];

  // Auto slide effect
  useEffect(() => {
    const timer = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % slides.length);
    }, 4500);
    return () => clearInterval(timer);
  }, [slides.length]);

  // Log screen size on mount
  useEffect(() => {
    console.log(
      `%c[Emberon Labs Debug] %cViewport Dimensions -> Width: %c${window.innerWidth}px%c, Height: %c${window.innerHeight}px`,
      "color: #39f59a; font-weight: bold;",
      "color: #e7efeb;",
      "color: #27d3ee; font-weight: bold;",
      "color: #e7efeb;",
      "color: #27d3ee; font-weight: bold;"
    );
  }, []);



  // Inquiry Form state
  const [form, setForm] = useState({ name: '', email: '', company: '', phone: '', vertical: 'mobility', details: '' });
  const [formStatus, setFormStatus] = useState('idle');
  const [errMsg, setErrMsg] = useState('');

  const submitInquiry = async (e) => {
    e.preventDefault();
    if (formStatus === 'sending') return;
    setFormStatus('sending');
    setErrMsg('');
    try {
      const payload = {
        name: form.name,
        email: form.email,
        company: form.company,
        phone: form.phone,
        vertical: form.vertical,
        subject: `Corporate Inquiry [${form.vertical.toUpperCase()}] at ${form.company || '(unspecified)'}`,
        message: form.details
      };
      const res = await authFetch('/save_enquiry', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('Server responded with ' + res.status);
      setFormStatus('ok');
      setForm({ name: '', email: '', company: '', phone: '', vertical: 'mobility', details: '' });
    } catch (err) {
      setFormStatus('err');
      setErrMsg(err.message || 'Something went wrong.');
    }
  };

  // Scroll to section helper
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <>
      <Helmet>
        <title>Emberon Labs LLP — EV Infrastructure &amp; Industrial IoT R&amp;D</title>
        <meta name="description" content="Emberon Labs LLP builds OCPP-compliant EV charge management software (Chargify CMS) and Industrial IoT monitoring systems for CPOs, fleet operators, and OEM integrators in India." />
        <link rel="canonical" href="https://emberonlabs.in/" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "Organization",
                "@id": "https://emberonlabs.in/#organization",
                "name": "Emberon Labs LLP",
                "url": "https://emberonlabs.in",
                "logo": {
                  "@type": "ImageObject",
                  "url": "https://emberonlabs.in/bg_removed_white_text.png"
                },
                "contactPoint": {
                  "@type": "ContactPoint",
                  "telephone": "+91-9266031789",
                  "email": "info@emberonlabs.in",
                  "contactType": "sales",
                  "areaServed": "IN",
                  "availableLanguage": "English"
                },
                "sameAs": [
                  "https://www.linkedin.com/company/emberon-labs/"
                ],
                "description": "Deep-tech R&D lab building OCPP-compliant EV charging infrastructure software and Industrial IoT monitoring systems for CPOs, fleet operators, and manufacturers.",
                "areaServed": "IN"
              },
              {
                "@type": "SoftwareApplication",
                "@id": "https://chargify.co.in/#product",
                "name": "Chargify CMS",
                "applicationCategory": "BusinessApplication",
                "operatingSystem": "Web",
                "description": "OCPP 1.6j and 2.0.1 compliant charge management system for CPOs and fleet operators. Supports multi-tenant networks, energy telemetry, and smart charging.",
                "url": "https://chargify.co.in",
                "offers": {
                  "@type": "Offer",
                  "availability": "https://schema.org/InStock"
                },
                "provider": {
                  "@id": "https://emberonlabs.in/#organization"
                }
              },
              {
                "@type": "WebSite",
                "@id": "https://emberonlabs.in/#website",
                "url": "https://emberonlabs.in",
                "name": "Emberon Labs",
                "publisher": {
                  "@id": "https://emberonlabs.in/#organization"
                }
              }
            ]
          }
        `}</script>
      </Helmet>

      <Nav />

      <main style={{ position: 'relative', zIndex: 1 }} className="pt-[68px]">

        {/* ── SECTION 01: HERO & SLIDER ── */}
        <section className="relative pt-0 pb-0 border-b border-line overflow-hidden">
          <div className="grid lg:grid-cols-[0.95fr_1.05fr] xl:grid-cols-[0.9fr_1.1fr] items-center">
            {/* Hero Left Content */}
            <div className="reveal in flex flex-col justify-center px-5 sm:px-8 lg:pl-[max(2rem,calc((100vw-1240px)/2+2rem))] lg:pr-10 py-12 lg:py-16">
              <h1 className="mt-0 font-display font-700 leading-[0.98] text-[clamp(2.5rem,5.5vw,4.5rem)] text-ink max-w-[16ch]">
                Engineering research into <span className="text-ember">real-world</span> systems.
              </h1>
              <p className="mt-6 text-[clamp(1rem,1.4vw,1.15rem)] text-mute max-w-[50ch] leading-relaxed">
                We are a deep-tech R&amp;D lab building hardware + software solutions for a cleaner, smarter and more efficient world of mobility and industry.
              </p>

              <div className="mt-8 flex flex-wrap gap-3">
                <button onClick={() => scrollTo('solutions')}
                  className="inline-flex items-center gap-2 text-[13.5px] font-700 tracking-[0.12em] uppercase text-white px-6 py-3.5 rounded-xl transition-transform hover:scale-[1.03] active:scale-95 shadow-md shadow-emerald-600/20"
                  style={{ background: 'linear-gradient(100deg, var(--lead), var(--sec))' }}>
                  EXPLORE SOLUTIONS <Arrow className="w-4 h-4" />
                </button>
                <button onClick={() => scrollTo('capabilities')}
                  className="inline-flex items-center gap-2 text-[13.5px] font-600 tracking-[0.12em] uppercase text-slate-800 px-6 py-3.5 rounded-xl chip hover:bg-slate-50 transition-transform hover:scale-[1.03] active:scale-95 shadow-sm">
                  OUR CAPABILITIES
                </button>
              </div>
            </div>

            {/* Hero Right — Framed Image Slider Wrapper with slight right padding */}
            <div className="px-5 sm:px-8 lg:pl-2 lg:pr-6 xl:pr-10 py-6 lg:py-12">
              <div className="reveal in relative rounded-2xl border border-slate-200/80 bg-white shadow-xl shadow-slate-900/5 overflow-hidden" style={{ transitionDelay: '.12s' }}>
              {/* Info bar above image: Title + Status + Dots */}
              <div className="flex items-center justify-between px-4 sm:px-5 py-3 border-b border-slate-200/80 bg-slate-50/90">
                <div className="flex items-center gap-3">
                  <span className="text-[11.5px] font-700 tracking-wider text-ink uppercase">
                    {slides[activeSlide].title}
                  </span>
                  <span className={`text-[8.5px] font-700 tracking-widest px-2.5 py-0.5 rounded-full border uppercase ${slides[activeSlide].statusColor}`}>
                    {slides[activeSlide].status}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  {/* Navigation Dots */}
                  <div className="flex gap-1.5">
                    {slides.map((_, idx) => (
                      <button
                        key={idx}
                        onClick={() => setActiveSlide(idx)}
                        className={`w-2 h-2 rounded-full transition-all duration-300 ${activeSlide === idx ? 'w-5 bg-ember' : 'bg-slate-300 hover:bg-slate-400'}`}
                        aria-label={`Go to slide ${idx + 1}`}
                      />
                    ))}
                  </div>
                  {/* Platform link or badge */}
                  {slides[activeSlide].isExternal ? (
                    <a href={slides[activeSlide].link} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-[10px] font-700 tracking-wide px-3 py-1.5 rounded-full text-white transition-transform hover:scale-[1.03] active:scale-95 shadow-sm"
                      style={{ background: 'var(--lead)' }}>
                      Go to Platform <Arrow className="w-3 h-3 text-white" />
                    </a>
                  ) : (
                    <span className="text-[10px] font-600 px-3 py-1.5 rounded-full border border-slate-200 bg-white text-mute">
                      Upcoming
                    </span>
                  )}
                </div>
              </div>

              {/* Image container — aspect ratio reserves space before image loads (prevents CLS) */}
              <div className="relative overflow-hidden group aspect-video">
                {/* Glow behind image */}
                <div className="absolute -inset-6 blur-3xl opacity-15 pointer-events-none group-hover:opacity-25 transition-opacity"
                  style={{
                    background: slides[activeSlide].status === 'Live'
                      ? 'radial-gradient(circle, var(--lead-soft) 0%, transparent 70%)'
                      : 'radial-gradient(circle, var(--sec-soft) 0%, transparent 70%)'
                  }}>
                </div>

                {slides[activeSlide].isExternal ? (
                  <a href={slides[activeSlide].link} target="_blank" rel="noopener noreferrer" className="block h-full">
                    <img
                      src={slides[activeSlide].img}
                      alt={slides[activeSlide].title + ' — EV charge management platform by Emberon Labs'}
                      className="w-full h-full object-cover filter brightness-[1.02] transition-transform duration-500 group-hover:scale-[1.01]"
                      width="960"
                      height="540"
                      style={{ display: 'block' }}
                    />
                  </a>
                ) : (
                  <img
                    src={slides[activeSlide].img}
                    alt={slides[activeSlide].title + ' — platform by Emberon Labs'}
                    className="w-full h-full object-cover filter brightness-[1.02] transition-transform duration-500 group-hover:scale-[1.01]"
                    width="960"
                    height="540"
                    style={{ display: 'block' }}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

        {/* ── SECTION 02: WHO WE ARE ── */}
        <section className="relative py-16 sm:py-20 px-5 sm:px-8 border-b border-line bg-slate-50/50">
          <div className="max-w-[1240px] mx-auto grid lg:grid-cols-[0.75fr_1.25fr] gap-12 lg:gap-8 items-center">
            {/* Left Column: Heading and Subtext */}
            <div className="reveal">
              <Eyebrow>WHO WE ARE</Eyebrow>
              <h2 className="mt-5 font-display font-700 text-[clamp(2.2rem,4vw,3.2rem)] leading-[1.02] text-ink">
                We are <span className="text-ember">Emberonlabs LLP</span> — <br className="hidden sm:inline" />a research and development lab.
              </h2>
              <p className="mt-5 text-[15px] text-mute leading-relaxed">
                From first principles research to field-ready systems, we design, build and ship solutions that solve complex problems in E-Mobility and Industrial operations.
              </p>
            </div>

            {/* Right Column: 3 Horizontal Cards Side-by-Side */}
            <div className="grid gap-4 grid-cols-1 sm:grid-cols-3 lg:grid-cols-3 reveal" style={{ transitionDelay: '.08s' }}>
              {[
                {
                  title: 'Research',
                  altText: 'Deep-tech R&D research methodology at Emberon Labs',
                  desc: 'Deep domain research and system architecture definition.',
                  img: microscopeImg
                },
                {
                  title: 'Prototype',
                  altText: 'Rapid hardware and firmware prototyping',
                  desc: 'Rapid prototyping and iteration to validate real-world use.',
                  img: boxImg
                },
                {
                  title: 'Deploy',
                  altText: 'Production-grade EV and IoT system deployment',
                  desc: 'Production-grade systems deployed at scale.',
                  img: rocketImg
                }
              ].map((step, idx) => (
                <div key={idx} className="chip border border-slate-200/80 rounded-xl p-5 hover:bg-white hover:shadow-md hover:border-emerald-500/30 transition-all flex gap-4 items-start h-full">
                  <img src={step.img} alt={step.altText} className="w-14 h-14 object-contain shrink-0" />
                  <div>
                    <h4 className="font-display font-700 text-[14px] text-ink leading-none">{step.title}</h4>
                    <p className="mt-2 text-[12px] text-mute leading-normal">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── SECTION 03: SOLUTION VERTICALS ── */}
        <section id="solutions" className="relative py-16 sm:py-20 px-5 sm:px-8 border-b border-line scroll-mt-14">
          <div className="max-w-[1240px] mx-auto">
            <div className="text-center mb-10">
              <h2 className="mt-0 font-display font-700 text-[clamp(2.2rem,4vw,3.2rem)] text-ink">
                Core Domains of Innovation
              </h2>
              <p className="mt-4 text-mute mx-auto text-[15px] max-w-[60ch]">
                We deploy hardware and software stacks specialized for modern grid operations and industrial factory floor monitoring.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
              {/* E-Mobility solutions */}
              <div id="e-mobility-vertical" className="reveal border border-slate-200/80 rounded-2xl bg-white p-8 sm:p-10 flex flex-col justify-between shadow-sm hover:shadow-md hover:border-emerald-500/40 transition-all scroll-mt-20">
                <div>
                  <div className="inline-flex items-center gap-3">
                    <img src={eMobilityImg} alt="EV charging infrastructure — E-Mobility Solutions by Emberon Labs" className="w-12 h-12 object-contain shrink-0" />
                    <h3 className="font-display text-[1.6rem] font-700 text-ink">E-Mobility Solutions</h3>
                  </div>
                  <p className="mt-6 text-[14.5px] text-mute leading-relaxed">
                    Building intelligent software ecosystems and hardware integrations to regulate, optimize, and orchestrate electric vehicle power networks.
                  </p>

                  <ul className="mt-8 space-y-4">
                    {[
                      ['Chargify CMS', 'Smart charger management supporting OCPP 1.6j/2.0.1 compliance & multi-tenant networks.'],
                      ['OCPP Compliance Layer', 'Cloud-native protocol orchestration, credential cycling, and charger diagnostics.'],
                      ['Energy Telemetry & Tariffs', 'Dynamic power allocation, automated transaction settlement & load scheduling.']
                    ].map(([t, d]) => (
                      <li key={t} className="flex items-start gap-3">
                        <span className="mt-1 grid place-items-center w-5 h-5 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200 shrink-0">
                          <Check className="w-3 h-3" />
                        </span>
                        <span className="text-[13.5px] text-mute">
                          <strong className="text-ink">{t}</strong> — {d}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Industrial Intelligence solutions */}
              <div id="industrial-vertical" className="reveal border border-slate-200/80 rounded-2xl bg-white p-8 sm:p-10 flex flex-col justify-between shadow-sm hover:shadow-md hover:border-cyan-500/40 transition-all scroll-mt-20">
                <div>
                  <div className="inline-flex items-center gap-3">
                    <img src={factoryImg} alt="Industrial IoT monitoring for manufacturing — Smart Industrial Suite" className="w-12 h-12 object-contain shrink-0" />
                    <h3 className="font-display text-[1.6rem] font-700 text-ink">Industrial Intelligence</h3>
                  </div>
                  <p className="mt-6 text-[14.5px] text-mute leading-relaxed">
                    Empowering manufacturing facilities with hardware-to-cloud networks that collect metrics directly from production stages and machines.
                  </p>

                  <ul className="mt-8 space-y-4">
                    {[
                      ['PMMS', 'Production Monitoring & Management System displaying stage statuses and net target outputs.'],
                      ['MMMS', 'Machine Monitoring & Management System assessing uptime, late starts, and shift efficiency.'],
                      ['Embedded Edge Acquisition', 'Rugged telemetry devices engineered to gather hardware signals and sync to cloud.']
                    ].map(([t, d]) => (
                      <li key={t} className="flex items-start gap-3">
                        <span className="mt-1 grid place-items-center w-5 h-5 rounded-md bg-cyan-50 text-cyan-700 border border-cyan-200 shrink-0">
                          <Check className="w-3 h-3" />
                        </span>
                        <span className="text-[13.5px] text-mute">
                          <strong className="text-ink">{t}</strong> — {d}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>


        {/* ── SECTION 05: OUR CAPABILITIES (R&D FOCUS) ── */}
        <section id="capabilities" className="relative py-16 sm:py-20 px-5 sm:px-8 border-b border-line scroll-mt-14">
          <div className="max-w-[1240px] mx-auto">
            <div className="mb-12 reveal">
              <h2 className="mt-0 font-display font-700 text-[clamp(2.2rem,4vw,3.2rem)] text-ink">
                Our Capabilities
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {[
                {
                  title: 'Embedded Systems Design',
                  altText: 'Embedded systems hardware design and PCB development',
                  list: ['Hardware design', 'PCB & schematics', 'Testing & validation'],
                  icon: chipImg
                },
                {
                  title: 'Cloud Platforms',
                  altText: 'Scalable cloud platform architecture for IoT and EV systems',
                  list: ['Scalable architecture', 'Secure APIs', 'DevOps & CI/CD'],
                  icon: cloudImg
                },
                {
                  title: 'Data Analytics',
                  altText: 'Real-time data analytics and ML forecasting for industrial IoT',
                  list: ['Real-time analytics', 'ML & forecasting', 'Insights & alerts'],
                  icon: analysisImg
                },
                {
                  title: 'IoT Connectivity',
                  altText: 'IoT connectivity with 4G, 5G, MQTT, and edge-to-cloud sync',
                  list: ['4G / 5G / LTE', 'MQTT / CoAP', 'Edge to cloud sync'],
                  icon: iotImg
                },
                {
                  title: 'Control & Monitoring Dashboards',
                  altText: 'Control and monitoring dashboards for EV charging and industrial systems',
                  list: ['Real-time monitoring', 'Custom dashboards', 'Alerts & notifications'],
                  icon: dashboardImg
                },
                {
                  title: 'Firmware / Edge Intelligence',
                  altText: 'Firmware development and edge AI intelligence for embedded systems',
                  list: ['RTOS / Linux', 'Edge ML models', 'OTA updates'],
                  icon: boxImg
                }
              ].map((cap, idx) => (
                <div key={idx} className="reveal border border-slate-200/80 rounded-xl bg-white p-5 shadow-sm hover:shadow-md hover:border-emerald-500/30 transition-all flex flex-col items-center text-center h-full">
                  <img src={cap.icon} alt={cap.altText} className="w-14 h-14 object-contain mb-5 shrink-0" />
                  <h3 className="font-display font-700 text-[13px] text-ink leading-snug min-h-[38px] flex items-center justify-center">{cap.title}</h3>
                  <ul className="mt-5 space-y-2 text-[12px] text-mute w-full">
                    {cap.list.map((item, i) => (
                      <li key={i} className="flex items-start gap-1.5 justify-start text-left">
                        <span className="text-emerald-600 font-bold shrink-0">✓</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── SECTION 06: OUR IDEA TO IMPACT (PIPELINE) ── */}
        <section className="relative py-20 px-5 sm:px-8 border-b border-line bg-slate-50/50">
          <div className="max-w-[1240px] mx-auto">
            <div className="mb-16 reveal">
              <h2 className="mt-0 font-display font-700 text-[clamp(2.2rem,4vw,3.2rem)] text-ink">
                Our Idea to Impact
              </h2>
            </div>

            <div className="relative">
              {/* Horizontal connecting line on desktop */}
              <div className="absolute top-[48px] left-[8%] right-[8%] h-0.5 bg-gradient-to-r from-emerald-200 via-cyan-200 to-emerald-200 z-0 hidden lg:block"></div>

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 relative z-10">
                {[
                  {
                    label: 'Discover',
                    desc: 'Understand problems & user needs',
                    icon: <img src={discoverImg} alt="Problem discovery and user research for EV and IoT systems" className="w-10 h-10 object-contain" />
                  },
                  {
                    label: 'Research',
                    desc: 'Deep research & system architecture',
                    icon: <img src={researchImg} alt="Deep technical research and system architecture design" className="w-10 h-10 object-contain" />
                  },
                  {
                    label: 'Prototype',
                    desc: 'Build, iterate and prove concepts',
                    icon: <img src={boxImg} alt="Hardware and software prototyping and iteration" className="w-10 h-10 object-contain" />
                  },
                  {
                    label: 'Validate',
                    desc: 'Test in real-world conditions',
                    icon: <img src={validateImg} alt="Real-world testing and system validation" className="w-10 h-10 object-contain" />
                  },
                  {
                    label: 'Deploy',
                    desc: 'Ship production-grade systems',
                    icon: <img src={rocketImg} alt="Production deployment of EV charging and industrial IoT systems" className="w-10 h-10 object-contain" />
                  },
                  {
                    label: 'Optimize',
                    desc: 'Monitor, learn and continuously improve',
                    icon: <img src={optimizeImg} alt="Continuous monitoring and system optimization" className="w-10 h-10 object-contain" />
                  }
                ].map((step, idx) => (
                  <div key={idx} className="reveal flex flex-col items-center text-center group" style={{ transitionDelay: `${idx * 0.05}s` }}>
                    {/* Double Ring Circular Node */}
                    <div className="w-24 h-24 rounded-full border border-slate-200/90 bg-white flex items-center justify-center mb-6 relative hover:border-emerald-500/50 hover:scale-105 transition-all shadow-md group-hover:shadow-lg">
                      <div className="w-20 h-20 rounded-full border border-slate-100 bg-slate-50 flex items-center justify-center group-hover:bg-emerald-50/40 transition-colors">
                        {step.icon}
                      </div>
                    </div>
                    {/* Step Title & Subtext */}
                    <h3 className="font-display font-700 text-[14.5px] text-ink leading-none">{step.label}</h3>
                    <p className="mt-3.5 text-[11.5px] text-mute leading-relaxed max-w-[18ch] mx-auto">{step.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── SECTION 07: WORK HIGHLIGHTS ── */}
        <section className="relative py-16 sm:py-20 px-5 sm:px-8 border-b border-line">
          <div className="max-w-[1240px] mx-auto">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-12 reveal">
              <div>
                <Eyebrow>WORK HIGHLIGHTS</Eyebrow>
                <h2 className="mt-4 font-display font-700 text-[clamp(2.2rem,4vw,3.2rem)] text-ink">
                  Shipped Systems. Real Impact.
                </h2>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 reveal" style={{ transitionDelay: '.08s' }}>
              {[
                {
                  title: 'EV Charger CMS Rollout',
                  desc: 'Deployed across multiple public & captive charging networks.',
                  metric: '2,500+',
                  sub: 'Chargers Managed',
                  status: 'Live',
                  color: 'var(--lead)'
                },
                {
                  title: 'Production Monitoring',
                  desc: 'Real-time telemetry tracking across manufacturing facilities.',
                  metric: '98%',
                  sub: 'Data Uptime',
                  status: 'Live',
                  color: 'var(--lead)'
                },
                {
                  title: 'Machine Uptime Analytics',
                  desc: 'Edge-intelligence analytics to reduce late starts & optimize shifts.',
                  metric: '25%',
                  sub: 'Downtime Reduction',
                  status: 'Pilot',
                  color: '#d97706'
                }
              ].map((item, idx) => (
                <div key={idx} className="border border-slate-200/80 rounded-2xl bg-white p-6 flex flex-col justify-between shadow-sm hover:shadow-md transition-all">
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-[10px] font-mono text-slate-400">METRIC.0{idx + 1}</span>
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-600 blink"></span>
                    </div>
                    <h3 className="font-display text-[14px] font-700 text-ink leading-tight">{item.title}</h3>
                    <p className="mt-2 text-[12px] text-mute leading-relaxed">{item.desc}</p>
                  </div>

                  <div className="mt-8 pt-4 border-t border-slate-200/80">
                    <span className="font-display text-[2rem] font-700 leading-none block" style={{ color: item.color }}>{item.metric}</span>
                    <span className="text-[11.5px] text-mute block mt-1">{item.sub}</span>

                    <span className={`inline-block mt-3 text-[8.5px] font-700 px-2.5 py-0.5 rounded-full border tracking-wide uppercase ${item.status === 'Live' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
                      {item.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>


        {/* ── SECTION 09: PROJECT INQUIRY CTA & FORM ── */}
        <section id="contact-section" className="relative py-16 sm:py-20 px-5 sm:px-8 border-b border-line bg-slate-50/50">
          <div className="max-w-[1240px] mx-auto">
            <div className="reveal relative rounded-2xl border border-slate-200/80 overflow-hidden bg-white shadow-xl shadow-slate-900/5">
              <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(70% 120% at 20% 0%, rgba(5, 150, 105, 0.05), transparent 60%), radial-gradient(50% 80% at 80% 100%, rgba(2, 132, 199, 0.04), transparent 60%)' }}></div>
              <div className="relative grid lg:grid-cols-[1fr_1.1fr] gap-0">

                {/* Left Side: Info & DNA */}
                <div className="px-8 sm:px-12 py-14 sm:py-16 flex flex-col justify-center border-b lg:border-b-0 lg:border-r border-slate-200/80">
                  <Eyebrow>GET IN TOUCH</Eyebrow>
                  <h2 className="mt-5 font-display font-700 text-[clamp(2rem,3.8vw,3.2rem)] leading-[1.02] text-ink">
                    Have a system to build?<br />Let's design it together.
                  </h2>
                  <p className="mt-5 text-[14.5px] text-mute leading-relaxed max-w-[44ch]">
                    From research to deployment, we turn your hardware and software ideas into secure, scalable, and future-ready systems.
                  </p>

                  {/* Trust list */}
                  <div className="mt-8 space-y-3.5">
                    {[
                      'Deep Tech DNA — Embedded and cloud architecture experts.',
                      'End-to-End Ownership — PCB layouts to telemetry software.',
                      'Built for Impact — Robust, field-ready platforms.'
                    ].map((txt, i) => (
                      <div key={i} className="flex items-center gap-3 text-[13.5px] text-mute">
                        <span className="w-5 h-5 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200 grid place-items-center shrink-0">
                          <Check className="w-3.5 h-3.5" />
                        </span>
                        {txt}
                      </div>
                    ))}
                  </div>

                  <div className="mt-10 flex flex-wrap gap-3">
                    <a href="mailto:info@emberonlabs.in" className="inline-flex items-center gap-2 text-[13.5px] font-600 tracking-[0.12em] uppercase text-slate-800 px-5 py-3 rounded-xl chip hover:bg-slate-50 shadow-sm">
                      SEND AN EMAIL
                    </a>
                  </div>
                </div>

                {/* Right Side: Form */}
                <div className="px-8 sm:px-12 py-14 sm:py-16">
                  {formStatus === 'ok' ? (
                    <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-8 text-center h-full flex flex-col items-center justify-center">
                      <span className="grid place-items-center w-14 h-14 rounded-full mx-auto" style={{ background: 'rgba(5, 150, 105, 0.12)' }}>
                        <Check className="w-7 h-7 text-emerald-700" />
                      </span>
                      <h3 className="mt-5 font-display text-[1.4rem] font-700 text-ink">Inquiry Saved.</h3>
                      <p className="mt-2 text-[14px] text-mute max-w-[34ch] mx-auto">
                        Thank you for reaching out. Our systems engineers will evaluate your requirements and contact you shortly.
                      </p>
                      <button onClick={() => setFormStatus('idle')}
                        className="mt-6 text-[13px] font-600 text-mute hover:text-ink transition-colors underline underline-offset-2">
                        Send another inquiry
                      </button>
                    </div>
                  ) : (
                    <form onSubmit={submitInquiry} className="space-y-4">
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-[12px] font-600 text-slate-700 mb-1.5">Full Name</label>
                          <input required className="w-full bg-slate-50/70 border border-slate-200 rounded-lg px-3.5 py-2.5 text-[14px] text-ink placeholder:text-slate-400 outline-none focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/10 transition"
                            value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="John Doe" />
                        </div>
                        <div>
                          <label className="block text-[12px] font-600 text-slate-700 mb-1.5">Work Email</label>
                          <input required type="email" className="w-full bg-slate-50/70 border border-slate-200 rounded-lg px-3.5 py-2.5 text-[14px] text-ink placeholder:text-slate-400 outline-none focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/10 transition"
                            value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="john@company.com" />
                        </div>
                      </div>

                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-[12px] font-600 text-slate-700 mb-1.5">Company Name</label>
                          <input className="w-full bg-slate-50/70 border border-slate-200 rounded-lg px-3.5 py-2.5 text-[14px] text-ink placeholder:text-slate-400 outline-none focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/10 transition"
                            value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder="Operator LLC" />
                        </div>
                        <div>
                          <label className="block text-[12px] font-600 text-slate-700 mb-1.5">Phone Number</label>
                          <input className="w-full bg-slate-50/70 border border-slate-200 rounded-lg px-3.5 py-2.5 text-[14px] text-ink placeholder:text-slate-400 outline-none focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/10 transition"
                            value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+91 98765 43210" />
                        </div>
                      </div>

                      <div>
                        <label className="block text-[12px] font-600 text-slate-700 mb-1.5">What are you building?</label>
                        <select className="w-full bg-slate-50/70 border border-slate-200 rounded-lg px-3.5 py-2.5 text-[14px] text-ink outline-none focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/10 transition"
                          value={form.vertical} onChange={(e) => setForm({ ...form, vertical: e.target.value })}>
                          <option value="mobility">E-Mobility Solutions (EV CMS/Fleets)</option>
                          <option value="industrial">Industrial IoT Intelligence (PMMS/MMMS)</option>
                          <option value="both">Both segments / Custom R&amp;D</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-[12px] font-600 text-slate-700 mb-1.5">Project Details</label>
                        <textarea required className="w-full bg-slate-50/70 border border-slate-200 rounded-lg px-3.5 py-2.5 text-[14px] text-ink placeholder:text-slate-400 outline-none focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/10 transition resize-y min-h-[100px]"
                          value={form.details} onChange={(e) => setForm({ ...form, details: e.target.value })} placeholder="Describe your telemetry needs, physical devices or operations timeline..." rows={4} />
                      </div>

                      {formStatus === 'err' && (
                        <p className="text-[12.5px] text-red-500">Couldn't send inquiry ({errMsg}). Please email directly.</p>
                      )}

                      <button type="submit" disabled={formStatus === 'sending'}
                        className="w-full inline-flex items-center justify-center gap-2 text-[14px] font-700 tracking-[0.12em] uppercase text-white px-6 py-3.5 rounded-xl transition-transform hover:scale-[1.01] active:scale-[0.99] disabled:opacity-60 shadow-md shadow-emerald-600/20"
                        style={{ background: 'linear-gradient(100deg, var(--lead), var(--sec))' }}>
                        {formStatus === 'sending' ? 'SENDING...' : <>SEND INQUIRY <Arrow className="w-4 h-4" /></>}
                      </button>
                    </form>
                  )}
                </div>

              </div>
            </div>
          </div>
        </section>

      </main>

      {/* ── FOOTER: Custom consolidated corporate footer ── */}
      <Footer hideForm={true} />
    </>
  );
}
